/**
 * First-frame poster cache: once a scene video decodes, its first frame is
 * remembered as a small JPEG data URL so the next visit can paint *something*
 * instantly instead of a white canvas while the video streams.
 *
 * Best-effort by design: capture silently skips tainted canvases, entries are
 * bounded (LRU), and a missing poster simply means the old white-canvas path.
 * Keys are canonical blob paths (SAS-stripped) so token refreshes reuse them.
 */

interface PosterEntry {
  dataUrl: string;
  lastUsed: number;
}

const posters = new Map<string, PosterEntry>();
const MAX_POSTERS = 24;

export const rememberVideoPoster = (key: string | null | undefined, dataUrl: string | null | undefined): void => {
  if (!key || !dataUrl) return;
  posters.delete(key);
  posters.set(key, { dataUrl, lastUsed: Date.now() });
  while (posters.size > MAX_POSTERS) {
    const oldest = posters.keys().next().value;
    if (oldest === undefined) break;
    posters.delete(oldest);
  }
};

export const getVideoPoster = (key: string | null | undefined): string | null => {
  if (!key) return null;
  const entry = posters.get(key);
  if (!entry) return null;
  // LRU touch: recently painted scenes survive eviction.
  posters.delete(key);
  posters.set(key, { ...entry, lastUsed: Date.now() });
  return entry.dataUrl;
};
